import { Pencil, Plus, RefreshCw, Trash2, UsersRound } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import notify from '../../lib/notify';
import { extractApiErrorMessage } from '../../lib/apiError';
import { Button } from '../Common/button';
import Modal from '../Common/Modal';
import ConfirmationModal from '../Common/ConfirmationModal';
import TableSkeleton from '../Common/TableSkeleton';
import {
  CreateClientRequest,
  ForgeClient,
  UpdateClientRequest,
} from '../../types';

type ClientFormState = {
  name: string;
  cpu: string;
  memory: string;
  pods: string;
  model_embedding: string;
  vector_dimensions: number;
  base_port: number;
};

const defaultCreateForm = (): ClientFormState => ({
  name: '',
  cpu: '8',
  memory: '16Gi',
  pods: '20',
  model_embedding: 'openai/text-embedding-ada-002',
  vector_dimensions: 1536,
  base_port: 8090,
});

const formatDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

export const ClientsTab: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<ForgeClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ClientFormState>(defaultCreateForm());
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [editClient, setEditClient] = useState<ForgeClient | null>(null);
  const [editForm, setEditForm] = useState<ClientFormState>(defaultCreateForm());
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteClient, setDeleteClient] = useState<ForgeClient | null>(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await forgeService.listClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients', err);
      setError(t('forgeDashboard.clients.loadError', 'Failed to load clients'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalForges = useMemo(
    () => clients.reduce((sum, c) => sum + (c.forge_ids?.length || 0), 0),
    [clients]
  );

  const openEdit = (c: ForgeClient) => {
    setEditClient(c);
    setEditForm({
      name: c.name,
      cpu: c.quotas?.cpu || '8',
      memory: c.quotas?.memory || '16Gi',
      pods: c.quotas?.pods || '20',
      model_embedding: 'openai/text-embedding-ada-002',
      vector_dimensions: 1536,
      base_port: 8090,
    });
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    setCreateSubmitting(true);
    try {
      const payload: CreateClientRequest = {
        name: createForm.name.trim(),
        quotas: { cpu: createForm.cpu, memory: createForm.memory, pods: createForm.pods },
        model_embedding: createForm.model_embedding,
        vector_dimensions: createForm.vector_dimensions,
        base_port: createForm.base_port,
      };
      await forgeService.createClient(payload);
      notify.success(t('forgeDashboard.clients.createSuccess', 'Client created'));
      setCreateOpen(false);
      setCreateForm(defaultCreateForm());
      fetchClients();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.clients.createError', 'Failed to create client')));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editClient) return;
    setEditSubmitting(true);
    try {
      const payload: UpdateClientRequest = {
        name: editForm.name.trim() || undefined,
        quotas: { cpu: editForm.cpu, memory: editForm.memory, pods: editForm.pods },
      };
      await forgeService.updateClient(editClient.client_id, payload);
      notify.success(t('forgeDashboard.clients.updateSuccess', 'Client updated'));
      setEditClient(null);
      fetchClients();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.clients.updateError', 'Failed to update client')));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteClient) return;
    setDeleteSubmitting(true);
    try {
      await forgeService.deleteClient(deleteClient.client_id);
      notify.success(t('forgeDashboard.clients.deleteSuccess', 'Client deleted'));
      setDeleteClient(null);
      setDeleteInput('');
      fetchClients();
    } catch (err) {
      notify.error(extractApiErrorMessage(err, t('forgeDashboard.clients.deleteError', 'Failed to delete client')));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <UsersRound className="h-4 w-4" />
          <span>
            {t('forgeDashboard.clients.summary', '{{count}} clients · {{forges}} forges', {
              count: clients.length,
              forges: totalForges,
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchClients} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('global.refresh', 'Refresh')}
          </Button>
          <Button onClick={() => { setCreateForm(defaultCreateForm()); setCreateOpen(true); }} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('forgeDashboard.clients.new', 'New client')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-destructive">{error}</div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : clients.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {t('forgeDashboard.clients.empty', 'No clients yet')}
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.clients.name', 'Name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.clients.quotas', 'Quotas (cpu / mem / pods)')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.clients.forges', 'Forges')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.clients.createdAt', 'Created')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('forgeDashboard.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {clients.map((c) => (
                  <tr key={c.client_id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-foreground">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-xs" title={c.client_id}>
                        {c.client_id}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground/80">
                      {c.quotas
                        ? `${c.quotas.cpu} / ${c.quotas.memory} / ${c.quotas.pods}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground/80">{c.forge_ids?.length || 0}</td>
                    <td className="px-4 py-3 text-sm text-foreground/80">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(c)} className="gap-1.5">
                          <Pencil className="h-3.5 w-3.5" />
                          {t('global.edit', 'Edit')}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => { setDeleteClient(c); setDeleteInput(''); }}
                          className="gap-1.5"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('global.delete', 'Delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create modal */}
      <Modal
        open={createOpen}
        title={t('forgeDashboard.clients.newTitle', 'Create Forge client')}
        description={t('forgeDashboard.clients.newDescription', 'Create a new Forge client (super-admin only).')}
        onClose={() => setCreateOpen(false)}
        hideDefaultFooter
        footer={
          <>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
              {t('global.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={createSubmitting || !createForm.name.trim()}>
              {createSubmitting ? t('global.saving', 'Saving…') : t('global.create', 'Create')}
            </Button>
          </>
        }
      >
        <ClientFormFields value={createForm} onChange={setCreateForm} />
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!editClient}
        title={t('forgeDashboard.clients.editTitle', 'Edit Forge client')}
        onClose={() => setEditClient(null)}
        hideDefaultFooter
        footer={
          <>
            <Button variant="outline" onClick={() => setEditClient(null)} disabled={editSubmitting}>
              {t('global.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleUpdate} disabled={editSubmitting}>
              {editSubmitting ? t('global.saving', 'Saving…') : t('global.save', 'Save')}
            </Button>
          </>
        }
      >
        <ClientFormFields value={editForm} onChange={setEditForm} hideAdvanced />
      </Modal>

      {/* Delete confirmation */}
      <ConfirmationModal
        open={!!deleteClient}
        title={t('forgeDashboard.clients.deleteTitle', 'Delete Forge client?')}
        message={t(
          'forgeDashboard.clients.deleteMessage',
          'This will permanently delete client "{{name}}" and all its forges in Forge. This cannot be undone.',
          { name: deleteClient?.name || '' }
        )}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        loading={deleteSubmitting}
        requireTextMatch={
          deleteClient
            ? { text: deleteClient.name, label: t('forgeDashboard.confirmTypeName', 'Type the name to confirm') }
            : undefined
        }
        inputValue={deleteInput}
        onInputChange={setDeleteInput}
        onConfirm={handleDelete}
        onCancel={() => { setDeleteClient(null); setDeleteInput(''); }}
      />
    </div>
  );
};

const ClientFormFields: React.FC<{
  value: ClientFormState;
  onChange: (v: ClientFormState) => void;
  hideAdvanced?: boolean;
}> = ({ value, onChange, hideAdvanced }) => {
  const { t } = useTranslation();
  const set = <K extends keyof ClientFormState>(k: K, v: ClientFormState[K]) =>
    onChange({ ...value, [k]: v });
  const inputCls =
    'w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">{t('forgeDashboard.clients.name', 'Name')}</label>
        <input className={inputCls} value={value.name} onChange={(e) => set('name', e.target.value)} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">CPU</label>
          <input className={inputCls} value={value.cpu} onChange={(e) => set('cpu', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('forgeDashboard.resources.memory', 'Memory')}</label>
          <input className={inputCls} value={value.memory} onChange={(e) => set('memory', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('forgeDashboard.resources.pods', 'Pods')}</label>
          <input className={inputCls} value={value.pods} onChange={(e) => set('pods', e.target.value)} />
        </div>
      </div>
      {!hideAdvanced && (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.clients.modelEmbedding', 'Embedding model')}
            </label>
            <input
              className={inputCls}
              value={value.model_embedding}
              onChange={(e) => set('model_embedding', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.clients.vectorDimensions', 'Vector dimensions')}
            </label>
            <input
              type="number"
              className={inputCls}
              value={value.vector_dimensions}
              onChange={(e) => set('vector_dimensions', Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forgeDashboard.clients.basePort', 'Base port')}
            </label>
            <input
              type="number"
              className={inputCls}
              value={value.base_port}
              onChange={(e) => set('base_port', Number(e.target.value) || 0)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsTab;
